import { Button, H3, InputLabel, InputWithUnit, TextArea, TextInput, ValueListInput } from '@components/Common';
import OperationLoader from '@components/Common/Loaders/OperationLoader';
import { SidebarButtons, SideForm } from '@components/Common/SideBarLayout';
import { Product, ProductAlias } from '@api/queries/products';
import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components';
import ProductAliases from './productAliases';

const RemoveButton = styled(Button)`
  margin-right: 15px;
`;

const WaybillDescription = styled(TextArea)`
  height: 100px;
`;


interface ComponentProps {
  product?: Product;
  save: (product) => void;
  close: () => void;
  remove: () => void;
  isSaving: boolean;
}

type Props = ComponentProps & WithTranslation;

interface State {
  code: string;
  aliases: ProductAlias[];
  aliasesFromInput: ProductAlias;
  name: string;
  density: string;
  notes: string;
  concentration: string;
  waybillDescription: string;
}

class ProductEditor extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    const product = props.product;

    this.state = {
      code: (product && product.code) || '',
      aliases: (product && product.aliases) || [],
      aliasesFromInput: null,
      name: (product && product.name) || '',
      density: (product && product.density) || '',
      notes: (product && product.notes) || '',
      concentration: (product && product.concentration) || '',
      waybillDescription: (product && product.waybillDescription) || ''

    };

    this.onAliasesChanged = this.onAliasesChanged.bind(this);
    this.onAliasInputChanged = this.onAliasInputChanged.bind(this);
    this.onFieldChanged = this.onFieldChanged.bind(this);
    this.onDecimalChanged = this.onDecimalChanged.bind(this);
    this.onSave = this.onSave.bind(this);
    this.onRemove = this.onRemove.bind(this);
  }

  public render() {
    const { aliases, code, concentration: concentration, name, density, notes, waybillDescription } = this.state;
    const { t, isSaving } = this.props;
    const isEdit = this.props.product !== null;

    return (
      <SideForm onSubmit={this.onSave} onEscape={this.props.close}>
        {isEdit ? (
          <H3>
            {t('editing')}: <i>{this.props.product.name}</i>
          </H3>
        ) : (
          <H3>
            {t('add_new')} {t('product.product').toLowerCase()}
          </H3>
        )}
        <div>
          <InputLabel htmlFor="code">{t('product.code')} *</InputLabel>
          <TextInput id="code" name="code" type="text" value={code} onChange={this.onFieldChanged} autoFocus />
        </div>
        <div>
          <InputLabel htmlFor="name">{t('product.name')} *</InputLabel>
          <TextInput id="name" name="name" type="text" value={name} onChange={this.onFieldChanged} />
        </div>
        <div>
          <ProductAliases values={aliases} onChange={this.onAliasesChanged} onInputChanged={this.onAliasInputChanged} />
        </div>
        <div>
          <InputLabel htmlFor="density">{t('product.density')}</InputLabel>
          <InputWithUnit>
            <TextInput
              id="density"
              name="density"
              type="number"
              step="any"
              value={density}
              onChange={this.onDecimalChanged}
            />
            <span>kg/ltr</span>
          </InputWithUnit>
        </div>
        <div>
          <InputLabel htmlFor="concentration">{t('product.concentration')}</InputLabel>
          <InputWithUnit>
            <TextInput
              id="concentration"
              name="concentration"
              type="number"
              step="any"
              value={concentration}
              onChange={this.onDecimalChanged}
            />
            <span>%</span>
          </InputWithUnit>
        </div>
        <div>
          <InputLabel htmlFor="notes">{t('product.notes')}</InputLabel>
          <TextArea id="notes" name="notes" value={notes} onChange={this.onFieldChanged} />
        </div>
        <div>
          <InputLabel htmlFor="waybillDescription">{t('product.waybill_description')}</InputLabel>
          <WaybillDescription
            id="waybillDescription"
            name="waybillDescription"
            value={waybillDescription}
            onChange={this.onFieldChanged} />
        </div>
        <SidebarButtons>
          <Button type="button" secondary={true} onClick={this.props.close}>
            {t('cancel')}
          </Button>
          <div />
          {isSaving && <OperationLoader title={t('saving') + '...'} />}
          {isEdit && (
            <RemoveButton type="button" danger={true} disabled={isSaving} onClick={this.onRemove}>
              {t('delete')}
            </RemoveButton>
          )}
          <Button type="submit" tertiary={true} disabled={!this.isValid() || isSaving}>
            {t('save')}
          </Button>
        </SidebarButtons>
      </SideForm>
    );
  }

  private onSave(e: React.FormEvent<any>) {
    e.preventDefault();

    const product = this.state;

    if (this.state.aliasesFromInput && this.state.aliasesFromInput.code) {
      product.aliases.push(this.state.aliasesFromInput);
    }

    this.props.save(product);
  }

  private onAliasesChanged(newValues: ProductAlias[]) {
    if (newValues.length > this.state.aliases.length) {
      this.setState({
        aliases: newValues,
        aliasesFromInput: null
      });
    } else {
      this.setState({
        aliases: newValues
      });
    }
  }

  private onAliasInputChanged(newValue: ProductAlias) {
    this.setState({
      aliasesFromInput: newValue
    });
  }

  private onFieldChanged(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;
    this.setState(prevState => ({
      ...prevState,
      [name]: value
    }));
  }

  private onDecimalChanged(e: React.ChangeEvent<HTMLInputElement>) {
    const value = isNaN(e.target.valueAsNumber) ? null : e.target.valueAsNumber;
    const name = e.target.name;

    this.setState(prevState => ({
      ...prevState,
      [name]: value
    }));
  }

  private isValid() {
    const { code, name } = this.state;
    return code.trim().length > 0 && name.trim().length > 0;
  }

  private onRemove() {
    if (confirm(this.props.t('product.confirm_delete'))) {
      this.props.remove();
    }
  }
}

export default withTranslation()(ProductEditor) as React.ComponentType<ComponentProps>;
